/**
 * RoleInstructions Component (Dual-Mode Support)
 *
 * Intermediate screen between Information Phase and Communication/Quiz Phase.
 * Shows role-specific instructions and post-learning assessments.
 *
 * DYAD Mode:
 * - Teacher (marker=0): The player who learned from AI/PDF/Web Search, will teach the Student
 *   → Shows post-learning state confidence (3-item scale + prediction)
 *   → Confidence questions asked AFTER learning (not before)
 * - Student (marker>0): The player who will learn from the Teacher
 *   → Shows "Ready to Learn" screen (confidence asked in PostChatReflection after learning)
 *
 * SINGLE Mode:
 * - Learner: The player who learned independently
 *   → Shows post-learning state confidence (3-item scale + prediction)
 *   → Confidence questions asked AFTER learning (not before)
 *
 * Data Storage:
 * - Teacher: player.set("preChatSelfAssessment", { learnedFrom, topicId, timestamp })
 * - Teacher: player.set("postLearning_state_confidence", { conf_understand, conf_answer })  (2-item; score predictions live in the quiz rounds)
 * - Student: No data saved here (confidence asked in PostChatReflection)
 * - Learner: player.set("learningConsolidation", { learnedFrom, topicId, timestamp })
 * - Learner: player.set("postLearning_state_confidence", { conf_understand, conf_answer })
 */

import React, { useState } from "react";
import { usePlayer, useGame } from "@empirica/core/player/classic/react";
import { ConfidenceScale, isConfidenceComplete, getEmptyConfidenceState } from "./ConfidenceScale";
import { ObjectivesChecklist, parseObjectives, buildCoverageRecord } from "./ObjectivesChecklist";

/**
 * Parse instruction string and render as bullet points
 * Format: "(1) Point one (2) Point two (3) Point three"
 * or "1. Point one 2. Point two 3. Point three"
 */
function LearningOutcomes({ instruction, color = "blue" }) {
  if (!instruction) return null;

  // Try to extract points from format like "(1) First (2) Second (3) Third"
  const parenMatch = instruction.match(/\(\d+\)\s*[^(]+/g);

  // Try to extract points from format like "1. First 2. Second 3. Third"
  const dotMatch = instruction.match(/\d+\.\s*[^0-9]+(?=\d+\.|$)/g);

  const colorClasses = {
    blue: "text-blue-800",
    emerald: "text-emerald-800",
    violet: "text-violet-800"
  };

  let points = [];
  if (parenMatch && parenMatch.length > 0) {
    points = parenMatch.map(p => p.replace(/^\(\d+\)\s*/, '').trim());
  } else if (dotMatch && dotMatch.length > 0) {
    points = dotMatch.map(p => p.replace(/^\d+\.\s*/, '').trim());
  }

  if (points.length > 0) {
    return (
      <ul className={`text-xs space-y-2 ${colorClasses[color]}`}>
        {points.map((point, idx) => (
          <li key={idx} className="flex items-start gap-2">
            <span className="flex-shrink-0 w-5 h-5 rounded-full bg-white flex items-center justify-center text-xs font-bold shadow-sm">
              {idx + 1}
            </span>
            <span className="pt-0.5 leading-relaxed">{point}</span>
          </li>
        ))}
      </ul>
    );
  }

  // Fallback: just show the instruction as text
  return (
    <p className={`text-xs leading-relaxed ${colorClasses[color]}`}>
      {instruction}
    </p>
  );
}

export function RoleInstructions({ next }) {
  const player = usePlayer();
  const game = useGame();

  const marker = player.get("marker");
  const secondaryMarker = player.get("secondary_marker");
  const config = game.get("config");
  const topicName = config?.name || "the topic";
  const gameMode = game.get("gameMode") || "dyad";
  const isSingleMode = gameMode === "single";

  // Determine role based on game mode
  const assignedRole = player.get("assignedRole");
  const isLearner = isSingleMode || assignedRole === "Learner";
  const isTeacher = !isSingleMode && marker === 0;
  const isStudent = !isSingleMode && marker > 0;

  // Check if self-assessment was already completed (Round 3)
  const preChatSelfAssessment = player.get("preChatSelfAssessment");
  const learningConsolidation = player.get("learningConsolidation");
  const selfAssessmentCompleted = !!preChatSelfAssessment || !!learningConsolidation;


  // Student's pre-communication confidence (3-item scale)
  const [studentConfidence, setStudentConfidence] = useState(getEmptyConfidenceState());

  // Learner's post-learning state confidence (2-item scale; prediction moved to quiz rounds) - single mode only
  const [learnerStateConfidence, setLearnerStateConfidence] = useState(getEmptyConfidenceState());

  // Teacher's post-learning state confidence (2-item scale; prediction moved to quiz rounds) - dyad mode
  const [teacherStateConfidence, setTeacherStateConfidence] = useState(getEmptyConfidenceState());

  // Teacher-only: topic-specific explaining self-efficacy after learning (0-6).
  // Second point of the explain-ability trajectory (baseline → postLearning → exit).
  // Asked as its own step AFTER the confidence/objectives — mirrors the baseline
  // explain step, which follows the baseline quiz.
  const [teacherExplainAbility, setTeacherExplainAbility] = useState(null);
  const [explainStep, setExplainStep] = useState(false);

  // Objectives coverage (Alice only): which of the topic's objectives her learning covered
  const objectives = parseObjectives(config?.instructionExplainer);
  const [objectivesCovered, setObjectivesCovered] = useState([]);
  const toggleObjective = (idx) =>
    setObjectivesCovered((prev) =>
      prev.includes(idx) ? prev.filter((i) => i !== idx) : [...prev, idx]
    );

  // Student's alternative topic review (loaded from Round 2)
  const alternativeReview = player.get("alternativeTopicReview");

  // For Teacher, we need state confidence (2-item scale)
  const teacherStateConfidenceComplete = isConfidenceComplete(teacherStateConfidence, []);
  const teacherReady = isTeacher
    ? (selfAssessmentCompleted || (teacherStateConfidenceComplete && teacherExplainAbility !== null))
    : true;

  // For Learner, we need state confidence (2-item scale)
  const learnerStateConfidenceComplete = isConfidenceComplete(learnerStateConfidence, []);
  const learnerReady = isLearner
    ? (selfAssessmentCompleted || learnerStateConfidenceComplete)
    : true;

  // Check if student has completed alternative topic review (confidence asked after learning in PostChatReflection)
  const studentReady = isStudent
    ? (alternativeReview?.submitted === true)
    : true;

  const handleStudentConfidenceChange = (key, value) => {
    setStudentConfidence((prev) => ({ ...prev, [key]: value }));
  };

  const handleLearnerStateConfidenceChange = (key, value) => {
    setLearnerStateConfidence((prev) => ({ ...prev, [key]: value }));
  };

  const handleTeacherStateConfidenceChange = (key, value) => {
    setTeacherStateConfidence((prev) => ({ ...prev, [key]: value }));
  };

  const handleNext = () => {
    if (isLearner && !selfAssessmentCompleted) {
      // Save learner's consolidation record (single mode)
      player.set("learningConsolidation", {
        learnedFrom: secondaryMarker,
        topicId: config?.id,
        timestamp: new Date().toISOString(),
      });
      // Save learner's post-learning state confidence (3-item scale + prediction)
      player.set("postLearning_state_confidence", {
        ...learnerStateConfidence,
        topicId: config?.id,
        timestamp: new Date().toISOString(),
      });
      // Save which objectives the learning covered
      player.set("postLearning_objectives_covered", {
        ...buildCoverageRecord(objectives, objectivesCovered),
        topicId: config?.id,
        timestamp: new Date().toISOString(),
      });
    } else if (isTeacher && !selfAssessmentCompleted) {
      // Save teacher's self-assessment record (only if not already completed)
      player.set("preChatSelfAssessment", {
        learnedFrom: secondaryMarker,
        topicId: config?.id,
        timestamp: new Date().toISOString(),
      });
      // Save teacher's post-learning state confidence (3-item scale + prediction)
      player.set("postLearning_state_confidence", {
        ...teacherStateConfidence,
        topicId: config?.id,
        timestamp: new Date().toISOString(),
      });
      // Save teacher's post-learning explaining self-efficacy (topic-specific)
      player.set("postLearning_explain_ability", {
        value: Number(teacherExplainAbility),
        topicId: config?.id,
        timestamp: new Date().toISOString(),
      });
      // Save which objectives the learning covered
      player.set("postLearning_objectives_covered", {
        ...buildCoverageRecord(objectives, objectivesCovered),
        topicId: config?.id,
        timestamp: new Date().toISOString(),
      });
    }
    // Students: No confidence questions here - they're asked after learning in PostChatReflection
    next();
  };

  // Learner (single mode): Consolidation view
  if (isLearner) {
    return (
      <div className="h-full w-full overflow-y-auto p-6 bg-gray-50">
        <div className="max-w-2xl mx-auto">
          {/* Header */}
          <div className="bg-violet-600 rounded-xl p-4 mb-6 text-white text-center">
            <span className="text-sm font-medium uppercase tracking-wide opacity-80">Learner</span>
            <h2 className="text-xl font-bold mt-1">{topicName}</h2>
            <p className="text-sm opacity-80 mt-1">Review Your Learning</p>
          </div>

          {/* Main Content */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            {/* Learning Outcomes */}
            <div className="bg-violet-50 border border-violet-200 rounded-lg p-4 mb-6 select-none">
              <h4 className="text-sm font-semibold text-violet-800 mb-2">Key Concepts You Should Know:</h4>
              <LearningOutcomes
                instruction={config?.instructionExplainer}
                color="violet"
              />
            </div>

            {/* Post-learning confidence (if not already completed) */}
            {!selfAssessmentCompleted && (
              <div className="mb-6 p-5 bg-violet-50 border border-violet-200 rounded-lg space-y-6">
                <h4 className="text-base font-semibold text-violet-900">Rate Your Confidence</h4>

                <ConfidenceScale
                  values={learnerStateConfidence}
                  onChange={handleLearnerStateConfidenceChange}
                  color="violet"
                  topicName={topicName}
                  startNumber={1}
                  showHeader={false}
                  includePrediction={false}
                />
              </div>
            )}

            {/* Objectives coverage (after learning) */}
            {!selfAssessmentCompleted && objectives.length > 0 && (
              <div className="mb-6 p-5 bg-violet-50 border border-violet-200 rounded-lg">
                <h4 className="text-base font-semibold text-violet-900 mb-1">Topics Covered</h4>
                <ObjectivesChecklist
                  objectives={objectives}
                  covered={objectivesCovered}
                  onToggle={toggleObjective}
                  color="violet"
                  instructions="Which of these did your learning cover? Tick all that apply."
                />
              </div>
            )}

            {/* Quiz reminder */}
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-6">
              <p className="text-sm text-amber-800">
                <strong>Next:</strong> You will complete a comprehension quiz on this topic.
              </p>
            </div>

            {/* Submit Button */}
            <button
              onClick={handleNext}
              disabled={!learnerReady}
              className={`w-full py-3 px-6 rounded-lg font-semibold text-white transition-all duration-200 ${
                learnerReady
                  ? 'bg-violet-600 hover:bg-violet-700'
                  : 'bg-gray-400 cursor-not-allowed'
              }`}
            >
              {learnerReady ? "Continue to Reflection" : "Please complete the ratings above"}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Teacher with completed self-assessment: Simple ready screen (no repetition of objectives)
  if (isTeacher && selfAssessmentCompleted) {
    return (
      <div className="h-full w-full overflow-y-auto p-6 bg-gray-50">
        <div className="max-w-2xl mx-auto">
          {/* Header */}
          <div className="bg-blue-600 rounded-xl p-4 mb-6 text-white text-center">
            <span className="text-sm font-medium uppercase tracking-wide opacity-80">Teacher</span>
            <h2 className="text-xl font-bold mt-1">{topicName}</h2>
          </div>

          {/* Main Content */}
          <div className="bg-white rounded-xl shadow-lg p-6 text-center">
            {/* Status */}
            <div className="mb-6">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <h3 className="text-xl font-semibold text-gray-800 mb-2">
                Your Partner is Preparing
              </h3>
              <p className="text-gray-600">
                They're reviewing example questions about the topic you'll teach them.
              </p>
            </div>

            {/* What happens next */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 text-left">
              <p className="text-sm text-blue-800">
                <strong>Next:</strong> Your partner hasn't seen any of the material you just studied. This topic is new to them, and you'll both be quizzed on it, so it's your job to teach them what they need to know.
              </p>
            </div>

            {/* Submit Button */}
            <button
              onClick={handleNext}
              className="w-full py-3 px-6 rounded-lg font-semibold text-white transition-all duration-200 bg-blue-600 hover:bg-blue-700"
            >
              I'm Ready to Teach
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Teacher first-time self-assessment: Single-column layout (matches Learner)
  if (isTeacher) {
    return (
      <div className="h-full w-full overflow-y-auto p-6 bg-gray-50">
        <div className="max-w-2xl mx-auto">
          {/* Header */}
          <div className="bg-blue-600 rounded-xl p-4 mb-6 text-white text-center">
            <span className="text-sm font-medium uppercase tracking-wide opacity-80">Teacher</span>
            <h2 className="text-xl font-bold mt-1">{topicName}</h2>
            <p className="text-sm opacity-80 mt-1">Prepare to Teach</p>
          </div>

          {/* Main Content */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            {!explainStep ? (
              <>
                {/* Step 1: post-learning confidence + objectives */}
                <div className="mb-6 space-y-4">
                  <h4 className="text-base font-semibold text-gray-800">Rate Your Confidence</h4>
                  <ConfidenceScale
                    values={teacherStateConfidence}
                    onChange={handleTeacherStateConfidenceChange}
                    color="blue"
                    topicName={topicName}
                    startNumber={1}
                    showHeader={false}
                    includePrediction={false}
                  />
                </div>

                {objectives.length > 0 && (
                  <div className="mb-6 space-y-2">
                    <h4 className="text-base font-semibold text-gray-800">Topics Covered</h4>
                    <ObjectivesChecklist
                      objectives={objectives}
                      covered={objectivesCovered}
                      onToggle={toggleObjective}
                      color="blue"
                      instructions="Which of these did your learning cover? Tick all that apply."
                    />
                  </div>
                )}

                <button
                  onClick={() => { if (selfAssessmentCompleted || teacherStateConfidenceComplete) setExplainStep(true); }}
                  disabled={!(selfAssessmentCompleted || teacherStateConfidenceComplete)}
                  className={`w-full py-3 px-6 rounded-lg font-semibold text-white transition-all duration-200 ${
                    (selfAssessmentCompleted || teacherStateConfidenceComplete) ? 'bg-blue-600 hover:bg-blue-700' : 'bg-gray-400 cursor-not-allowed'
                  }`}
                >
                  {(selfAssessmentCompleted || teacherStateConfidenceComplete) ? "Continue" : "Please complete the ratings above"}
                </button>
              </>
            ) : (
              <>
                {/* Step 2 (standalone): post-learning explaining self-efficacy —
                    mirrors the baseline explain step that follows the quiz. */}
                <div className="mb-6">
                  <p className="text-base font-semibold text-gray-800 mb-1">One last question before you teach</p>
                  <p className="text-sm font-medium text-gray-700 mb-1">
                    How well could you explain this topic to your partner right now?
                  </p>
                  <p className="text-xs text-gray-500 mb-3">(0 = Not at all well, 6 = Extremely well)</p>
                  <div className="flex justify-between items-center gap-2">
                    {[0, 1, 2, 3, 4, 5, 6].map((v) => (
                      <label
                        key={v}
                        className={`flex-1 text-center py-2 px-1 rounded-lg cursor-pointer transition-all ${
                          teacherExplainAbility === String(v) ? "bg-blue-600 text-white" : "bg-white border border-gray-300 hover:border-gray-400"
                        }`}
                      >
                        <input
                          type="radio"
                          name="postLearning_explain_ability"
                          value={v}
                          checked={teacherExplainAbility === String(v)}
                          onChange={(e) => setTeacherExplainAbility(e.target.value)}
                          className="sr-only"
                        />
                        {v}
                      </label>
                    ))}
                  </div>
                </div>

                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-6">
                  <p className="text-sm text-amber-800">
                    <strong>Next:</strong> Your partner hasn't seen the material you just studied. You'll teach them through a text chat and then you both take a quiz, so ensure you cover all the topics and teach them what they need to know.
                  </p>
                </div>

                <div className="flex items-center justify-between">
                  <button onClick={() => setExplainStep(false)} className="py-3 px-4 rounded-lg font-medium text-gray-700 hover:bg-gray-100">
                    Back
                  </button>
                  <button
                    onClick={handleNext}
                    disabled={teacherExplainAbility === null}
                    className={`py-3 px-6 rounded-lg font-semibold text-white transition-all duration-200 ${
                      teacherExplainAbility !== null ? 'bg-blue-600 hover:bg-blue-700' : 'bg-gray-400 cursor-not-allowed'
                    }`}
                  >
                    {teacherExplainAbility !== null ? "I'm Ready to Teach" : "Please answer the question above"}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Student: Ready to learn from partner
  return (
    <div className="h-full w-full overflow-y-auto p-6 bg-gray-50">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="bg-emerald-600 rounded-xl p-4 mb-6 text-white text-center">
          <span className="text-sm font-medium uppercase tracking-wide opacity-80">Student</span>
          <h2 className="text-xl font-bold mt-1">{topicName}</h2>
          <p className="text-sm opacity-80 mt-1">Ready to Learn</p>
        </div>

        {/* Main Content */}
        <div className="bg-white rounded-xl shadow-lg p-6">
          {/* What to Learn */}
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 mb-6 select-none">
            <h4 className="text-sm font-semibold text-emerald-800 mb-2">What You Need to Learn:</h4>
            <LearningOutcomes
              instruction={config?.instructionLearner}
              color="emerald"
            />
          </div>

          {/* Application Context */}
          {config?.applicationContextLearner && (
            <div className="bg-purple-50 border-2 border-purple-300 rounded-lg p-4 mb-6 select-none">
              <h4 className="text-sm font-semibold text-purple-800 mb-1">You Will Apply This To:</h4>
              <p className="text-sm text-purple-700">{config.applicationContextLearner}</p>
            </div>
          )}

          {/* Learning tips */}
          <div className="mb-6 p-4 bg-gray-50 border border-gray-200 rounded-lg">
            <h4 className="text-sm font-semibold text-gray-800 mb-2">Tips for Learning:</h4>
            <ul className="text-sm text-gray-700 space-y-1">
              <li>• Ask questions if something is unclear</li>
              <li>• Take notes on key points</li>
              <li>• Try to understand, not just memorize</li>
            </ul>
          </div>

          {/* Quiz reminder */}
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-6">
            <p className="text-sm text-amber-800">
              <strong>Remember:</strong> You will be tested on this topic at the end.
            </p>
          </div>

          {/* Submit Button */}
          <button
            onClick={handleNext}
            disabled={!studentReady}
            className={`w-full py-3 px-6 rounded-lg font-semibold text-white transition-all duration-200 ${
              studentReady
                ? 'bg-emerald-600 hover:bg-emerald-700'
                : 'bg-gray-400 cursor-not-allowed'
            }`}
          >
            {studentReady ? "I'm Ready to Learn" : "Please complete the alternative topic review"}
          </button>
        </div>
      </div>
    </div>
  );
}
